import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Row, Col, Card, Button, Spinner, Container } from 'react-bootstrap';
import { Formik, Form } from 'formik';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { PutJson, PostJson, GetJson } from '@awp/helpers/fetchReact';
import { REST_API, FORM_MODE, Permissions } from '@awp/constants';
import { FormInput, FormSelect, FormSwitch, AreaCodeInput } from '@awp/components/Common/AwpFormInputs';
import { isValueEmpty } from '@awp/helpers/utilsReact';
import AwpValidationErrorModal from '@awp/components/Common/AwpValidationErrorModal';
import { getAvalancheGeographicAreas } from '@awp/stores/avalancheSlice';
import AvalancheSubAreasAdmin from '../AvalancheSubAreasAdmin/AvalancheSubAreasAdmin';
import Authorize from '@awp/components/Authorize';
import AwpCloseCancelButtons from '../../components/AwpCloseCancelButton';
import isEqual from 'lodash/isEqual';
import { AvalancheAreasService } from '@components/AvalancheAreasAdmin/AvalancheAreasService';

const AvalancheAreasEdit = ({
  setSubAreaList,
  subAreaList,
  setMode,
  mode,
  data,
  setData,
  loading,
  areaList,
  setAreaList,
  subData,
  setSubData,
}) => {
  const programAreas = useSelector((state) => getAvalancheGeographicAreas(state, 'AVALANCHE PROGRAM'));
  const avalanchePrograms = mode === FORM_MODE.CREATE ? programAreas.filter((x) => !x.isDisabled) : programAreas;

  const [avalancheAreaData, setAvalancheAreaData] = useState(mode === FORM_MODE.CREATE ? {} : data);
  const [entityErrors, setEntityErrors] = useState(null);

  useEffect(() => {
    if (mode !== FORM_MODE.CREATE) loadAvalancheArea();
  }, [mode]);

  const hasActiveSubArea =
    subAreaList != null &&
    subAreaList.length > 0 &&
    subAreaList.some((x) => !x.isDisabled && x.parentId == avalancheAreaData.value);
  const avalancheAreasService = new AvalancheAreasService(mode);
  const { readOnly, title, successMessage } = avalancheAreasService.crud;

  // Load Avalanche Area
  const loadAvalancheArea = async () => {
    if (avalancheAreaData && avalancheAreaData.value) {
      try {
        const url = `${REST_API.NON_SPATIAL_REFERENCE_DATA}geographicavalanchearea/${avalancheAreaData.value}`;
        const response = await GetJson(url);
        const record = await response.json();

        if (record) {
          setAvalancheAreaData((prev) => ({
            ...prev,
            isDisabled: record.IsActive === 'N',
            ConcurrencyControlNumber: record.ConcurrencyControlNumber,
          }));
        }
      } catch (error) {
        console.error('Error loading Avalanche Area:', error);
      }
    }
  };
  // Update Area List
  const updateAreaList = (updatedArea) => {
    if (mode === FORM_MODE.EDIT) {
      const updatedList = areaList.map((item) => (item.value === updatedArea.value ? updatedArea : item));
      setAreaList(updatedList);
    } else {
      setAreaList([...areaList, updatedArea]);
    }
    setData(updatedArea);
  };
  // Create Avalanche Area
  const handleCreateAvalancheArea = async (setSubmitting, resetForm, setErrors, areaDto) => {
    try {
      const response = await PostJson(`${REST_API.NON_SPATIAL_REFERENCE_DATA}avalanchearea`, areaDto);
      if (response.ok) {
        const newArea = await response.json();

        const updatedArea = {
          value: newArea.Id,
          name: newArea.Name,
          code: newArea.Code,
          parentId: newArea.ParentGeographicAreaId,
          ConcurrencyControlNumber: newArea.ConcurrencyControlNumber,
          isDisabled: !newArea.IsActive,
        };
        const updatedDto = avalancheAreasService.mapInitialValues({
          entity: {
            ...areaDto,
            program: updatedArea,
          },
        });

        setAvalancheAreaData(updatedArea);
        updateAreaList(updatedArea);
        resetForm({
          values: updatedDto,
        });
        toast.success(successMessage);
        setMode(FORM_MODE.VIEW);
      } else if (response.status === 412) {
        const errors = await response.json();
        setErrors(errors);
        setEntityErrors({ ['Avalanche Area']: errors.entity });
      }
    } catch (error) {
      console.error('Error creating Avalanche Area:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Update Avalanche Area
  const handleUpdateAvalancheArea = async (setSubmitting, resetForm, setErrors, areaDto) => {
    try {
      const response = await PutJson(`${REST_API.NON_SPATIAL_REFERENCE_DATA}avalanchearea/${areaDto.value}`, areaDto);
      if (response.ok) {
        const updatedArea = await response.json();
        updateAreaList(areaDto);

        const updatedDto = avalancheAreasService.mapInitialValues({
          entity: {
            ...areaDto,
            program: avalanchePrograms.find((x) => x.value === updatedArea.ParentGeographicAreaId),
          },
        });

        resetForm({ values: updatedDto });
        toast.success(successMessage);
        setMode(FORM_MODE.VIEW);
      } else if (response.status === 412) {
        const errors = await response.json();
        setErrors(errors);

        const dto = avalancheAreasService.mapInitialValues({
          entity: {
            ...areaDto,
            program: avalanchePrograms.find((x) => x.value === avalancheAreaData.parentId),
          },
        });
        resetForm({ values: dto });

        setEntityErrors({ ['Avalanche Area']: errors.entity });
      }
    } catch (error) {
      console.error('Error updating Avalanche Area:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const programOnChange = (opt, setFieldValue) => {};

  const breadCrumbs = () => {
    setMode(FORM_MODE.LIST);
  };

  const handleSubmit = (values, { setSubmitting, resetForm, setErrors }) => {
    setSubmitting(true);

    const dto = avalancheAreasService.mapFormToDTO({ entity: avalancheAreaData, values });

    if (mode === FORM_MODE.EDIT) {
      handleUpdateAvalancheArea(setSubmitting, resetForm, setErrors, dto);
    } else {
      handleCreateAvalancheArea(setSubmitting, resetForm, setErrors, dto);
    }
  };

  const onClickCancel = () => {
    cancelOperation();
  };

  const onEditButtonClick = async () => {
    setMode(FORM_MODE.EDIT);
  };

  const cancelOperation = () => {
    setMode(FORM_MODE.LIST);
  };

  return (
    <>
      {loading ? (
        <Container>
          <Spinner animation="border" />
        </Container>
      ) : (
        <>
          <Formik
            enableReinitialize
            initialValues={avalancheAreasService.mapInitialValues({
              entity: {
                ...avalancheAreaData,
                program: avalanchePrograms.find((x) => x.value === avalancheAreaData.parentId),
              },
            })}
            validationSchema={Yup.object({
              program: Yup.object()
                .nullable()
                .test('program', 'Program is a required field', (value, context) => {
                  return (
                    (!isValueEmpty(value) && !isValueEmpty(value.value)) ||
                    (context && context.originalValue && context.originalValue.length > 0)
                  );
                }),
              name: Yup.string()
                .nullable()
                .max(50, 'Maximum 50 characters are allowed')
                .required('Avalanche Area Name is a required field'),
              code: Yup.string()
                .nullable()
                .length(5, 'Must be exactly 5 characters')
                .matches(/^\d{3}00$/, 'Must be 3 unique numbers followed by 00')
                .required('Avalanche Area Code is a required field'),
            })}
            onSubmit={handleSubmit}
          >
            {(props) => (
              <Form>
                <span className="awp-text-btn" onClick={breadCrumbs}>
                  List Avalanche Areas
                </span>
                <span className="mx-3">&gt;</span>
                <span>{title}</span>
                <Card border="light" className="page-card">
                  <Card.Header>
                    <Row className="mt-4"></Row>
                    {avalancheAreasService.crud.getHeader()}
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col sm={6}>
                        <FormSelect
                          className="avalanche-select"
                          readOnly={readOnly || mode === FORM_MODE.EDIT}
                          name="program"
                          id="program"
                          label="Program"
                          useMargin={true}
                          postChange={(e) => {
                            programOnChange(e, props.setFieldValue);
                          }}
                          options={avalanchePrograms}
                          isMulti={false}
                        />
                      </Col>
                      <Col sm={4} className="mb-4">
                        <AreaCodeInput
                          id="code"
                          readOnly={readOnly || mode === FORM_MODE.EDIT}
                          name="code"
                          label="Code"
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col sm={6} className="mb-4">
                        <FormInput
                          label="Avalanche Area Name"
                          instruction="(50 Characters max)"
                          readOnly={readOnly || mode === FORM_MODE.EDIT}
                          name="name"
                          id="name"
                        />
                      </Col>
                      <Col sm={6} className="mb-4">
                        <FormSwitch
                          useMargin={true}
                          readOnly={
                            readOnly || (mode == FORM_MODE.EDIT && !avalancheAreaData.isDisabled && hasActiveSubArea)
                          }
                          id="active"
                          name="active"
                          label="Active"
                          value={!avalancheAreaData.isDisabled}
                        />
                      </Col>
                    </Row>
                    {mode !== FORM_MODE.CREATE ? (
                      <Row>
                        <AvalancheSubAreasAdmin
                          subAreas={subAreaList}
                          setSubAreas={setSubAreaList}
                          avalancheAreaData={avalancheAreaData}
                          mode={mode}
                          setMode={setMode}
                          subData={subData}
                          setSubData={setSubData}
                        />
                      </Row>
                    ) : (
                      <></>
                    )}
                  </Card.Body>
                  <Card.Footer>
                    {!readOnly ? (
                      <AwpCloseCancelButtons
                        props={props}
                        isPristine={isEqual(
                          avalancheAreasService.mapInitialValues({ entity: props?.values }),
                          avalancheAreasService.mapInitialValues({
                            entity: {
                              ...avalancheAreaData,
                              program: avalanchePrograms.find((x) => x.value === avalancheAreaData.parentId),
                            },
                          }),
                        )}
                        hasAccess={true}
                        onConfirmedCancel={() => {
                          onClickCancel();
                        }}
                      />
                    ) : (
                      <Authorize requires={[Permissions.WriteAdminTools]}>
                        <div className="mr-3">
                          <Button variant="outline-primary" onClick={() => onClickCancel()}>
                            Close
                          </Button>
                          <Button className="ml-2" variant="outline-primary" onClick={onEditButtonClick}>
                            Edit Avalanche Area
                          </Button>
                        </div>
                      </Authorize>
                    )}
                  </Card.Footer>
                </Card>
              </Form>
            )}
          </Formik>
          <AwpValidationErrorModal setErrors={setEntityErrors} errors={entityErrors} />
        </>
      )}
    </>
  );
};

export default AvalancheAreasEdit;
